"use client";

import React, { useState } from "react";
import { Check, Loader, Calendar as CalendarIcon, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
const inputClasses = "flex h-11 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-secondary disabled:cursor-not-allowed disabled:opacity-50";
const textareaClasses = "flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-secondary disabled:cursor-not-allowed disabled:opacity-50";
function StrategyForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="2201" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="rounded-full bg-secondary/20 p-4">
          <Check className="size-8 text-secondary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Consultation Requested</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">
          {successMessage || "Your request is being reviewed. A senior consultant will reach out to confirm your session."}
        </p>
        <Button variant="outline" className="mt-4 border-secondary text-secondary hover:bg-secondary/5" onClick={() => window.location.reload()}>
          Request Another Time
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid gap-5 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Full Name</FormLabel>
              <FormControl>
                <input {...field} placeholder="e.g. Alexander Hamilton" className={inputClasses} type="text" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Direct Phone</FormLabel>
              <FormControl>
                <input {...field} placeholder="+1 (555) 000-0000" className={inputClasses} type="tel" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Work Email</FormLabel>
            <FormControl>
              <input {...field} placeholder="alex@company.com" className={inputClasses} type="email" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="business_category" rules={{
      required: "Please select your organization type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Organization Type</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="h-11 border-input focus:ring-secondary">
                  <SelectValue placeholder="Select your industry" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {["SME / Mid-Market", "Enterprise / Global 500", "Non-Profit / Government", "Technology Partner", "Other"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="goals" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Primary Transformation Goals</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="E.g. Automating supply chain communications or AI-driven customer insights..." className={textareaClasses} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
          {submitError}
        </div>}

      <Button type="submit" size="lg" className="w-full text-base font-bold h-12 bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all duration-300 rounded-lg" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Processing Request...
          </> : <>
            Schedule Consultation
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
      
      <p className="text-xs text-center text-muted-foreground mt-4 font-medium">
        Strictly confidential strategy session.
      </p>
    </Form>;
}
export default function InitiateYourForge() {
  const [date, setDate] = useState<Date | undefined>(new Date());
  const benefits = ["Current operational bottleneck assessment", "AI & Agentic solution feasibility study", "Scalable transformation strategy", "Legacy systems integration plan"];
  return <section id="start-your-transformation" className="bg-background text-foreground py-20 relative overflow-hidden border-2 border-secondary/40 rounded-[2rem] m-4 md:m-10">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-secondary/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-primary/5 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {}
          <div className="lg:col-span-5 space-y-10">
            <div className="space-y-6">
              
              
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-tight text-primary">
                Ready to Evolve Your <span className="text-secondary">Business?</span>
              </h2>
              
              <p className="text-lg text-slate-600 leading-relaxed font-medium">See the Agents in Action — Free 20-Minute Demo</p>
            </div>



            {}
            
          </div>

          {}
          <div className="lg:col-span-7">
            <Card className="border-border bg-white/95 backdrop-blur-md shadow-2xl shadow-slate-200/50 relative overflow-hidden rounded-2xl">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-secondary to-transparent opacity-80" />
              
              <CardHeader className="pb-2">
                <CardTitle className="text-2xl md:text-3xl font-extrabold text-primary">Start Your Transformation</CardTitle>
                <CardDescription className="text-base font-medium text-slate-500">No pitch. No proposal. Just 20 minutes and a live tool doing your exact job.</CardDescription>
              </CardHeader>
              
              <CardContent className="pt-6">
                <FormProvider formKey="transformation_form_home" formId="transformation_form_home" sectionName="consultation-section" defaultValues={{
                name: "",
                email: "",
                phone: "",
                business_category: "",
                goals: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                business_category: {
                  required: true
                },
                goals: {
                  required: false
                }
              }} submitText="Schedule Consultation" successMessage="Your request is being reviewed. A senior consultant will reach out to confirm your session.">
                  <StrategyForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}